import { useOrganizationStore } from "@/stores/useOrganizationStore";
import {
  useUserOrganizations,
  useOrganizationDetails,
  useOrganizationUsers,
  useOrganizationInvitations,
  useOrganizationTemplates,
} from "@/application/queries";
import { useCurrentOrganization } from "./useCurrentOrganization";
import { useCurrentOrganizationId } from "./useCurrentOrganizationId";
import { useOrganizationActions } from "./useOrganizationActions";
import { AuthContext } from "@/contexts/AuthContext";
import { useContext, useEffect } from "react";
interface OrganizationWorkflowOptions {
  pathname: string;
  search: string;
  loadDetails?: boolean;
  storage: {
    setCurrentOrgUuid: (uuid: string) => void;
  };
}

export const useOrganizations = ({
  pathname,
  search,
  storage,
  loadDetails = true,
}: OrganizationWorkflowOptions) => {
  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const tokenOrganizationUuid = isAuthenticated
    ? (authContext?.tokenManagement.getOrganizationUuid() ?? null)
    : null;

  const currentOrganization = useCurrentOrganization();
  const orgId = useCurrentOrganizationId();
  const setSelectedOrganizationUuid = useOrganizationStore((s) => s.setSelectedOrganizationUuid);

  // TQ queries
  const userOrganizationsQuery = useUserOrganizations(isAuthenticated);
  const organizationDetailsQuery = useOrganizationDetails(orgId, loadDetails);
  const organizationUsersQuery = useOrganizationUsers(orgId, loadDetails);
  const organizationInvitationsQuery = useOrganizationInvitations(orgId, loadDetails);
  const organizationTemplatesQuery = useOrganizationTemplates(orgId, false);

  const actions = useOrganizationActions(pathname, search);

  // The token decides the organization: the backend serves its data from it.
  // Without one in the token, fall back to the default membership, then the first.
  const memberships = userOrganizationsQuery.data;
  useEffect(() => {
    if (!isAuthenticated) return;
    const isMember = memberships?.some((o) => o.organization.uuid === orgId) ?? false;
    const fallback = memberships?.find((o) => o.is_default) ?? memberships?.[0];
    const targetUuid = tokenOrganizationUuid ?? (isMember ? orgId : fallback?.organization.uuid);
    if (!targetUuid || targetUuid === orgId) return;
    setSelectedOrganizationUuid(targetUuid);
    storage.setCurrentOrgUuid(targetUuid);
  }, [
    isAuthenticated,
    tokenOrganizationUuid,
    memberships,
    orgId,
    setSelectedOrganizationUuid,
    storage,
  ]);

  return {
    currentOrganization,
    userOrganizations: userOrganizationsQuery.data ?? [],
    organizationDetails: organizationDetailsQuery.data ?? null,
    organizationUsers: organizationUsersQuery.data ?? [],
    organizationInvitations: organizationInvitationsQuery.data ?? [],
    organizationTemplates: organizationTemplatesQuery.data ?? [],
    error: userOrganizationsQuery.error?.message ?? organizationDetailsQuery.error?.message ?? null,
    isLoadingOrganizations: userOrganizationsQuery.isLoading,
    isLoadingDetails: organizationDetailsQuery.isLoading,
    isLoadingUsers: organizationUsersQuery.isLoading || organizationUsersQuery.isFetching,
    isLoadingInvitations:
      organizationInvitationsQuery.isLoading || organizationInvitationsQuery.isFetching,
    isLoadingTemplates: organizationTemplatesQuery.isLoading,
    isUpdatingMember: false,
    retryUserOrganizations: () => void userOrganizationsQuery.refetch(),
    ...actions,
  };
};
